import React from "react";
import { useMailTemplate } from "../../../../contexts/api";
import { usePermissions } from "../../../../contexts/app/permissions";
import { useAlert } from "../../../../contexts/app/notificationToast";
import { ConfirmationModalContext } from "../../../../contexts/app/confirmation";
import type {
  MailPlaceholderDefinition,
  MailTemplate,
  MailTemplateType,
} from "api/mailTemplates/MailTemplate.typed";
import { MAIL_PLACEHOLDER_TYPE_BOOLEAN } from "api/mailTemplates/MailTemplate.typed";
import {
  mailEnvelopeRecipientPlaceholderChips,
  mailEnvelopeSubjectPlaceholderChips,
  mailPlaceholderChipAllowDrag,
  mailPlaceholderInsertKind,
  mailPlaceholderInteractiveKeys,
  mailPlaceholderPaletteChips,
  type MailPlaceholderInsertTarget,
} from "../mailPlaceholderContext";
import {
  emptyBlocksForNewTemplate,
  hydrateBlocksFromStoredHtml,
  serializeBlocks,
} from "../mailBodyBlocks";
import { mailPhToken } from "../mailLineParts";
import type { MailBodyBlock } from "../mailBodyBlocks";
import type { MailBodyBlockEditorHandle } from "../MailBodyBlockEditor";

function hydrateTemplate(raw: MailTemplate): { template: MailTemplate; blocks: MailBodyBlock[] } {
  const { blocks: nextBlocks, html } = hydrateBlocksFromStoredHtml(raw.html || "");
  const to = Array.isArray(raw.to) ? raw.to : [];
  const bcc = Array.isArray(raw.bcc) ? raw.bcc : [];

  return { template: { ...raw, html, to, bcc }, blocks: nextBlocks };
}

function emptyDraft(type: MailTemplateType): { template: MailTemplate; blocks: MailBodyBlock[] } {
  const blocks = emptyBlocksForNewTemplate();

  return {
    template: {
      name: "",
      type,
      subject: "",
      html: serializeBlocks(blocks),
      to: [],
      bcc: [],
    },
    blocks,
  };
}

export function useMailTemplatesPageState() {
  const {
    listMailTemplates,
    createMailTemplate,
    updateMailTemplate,
    deleteMailTemplate,
    listMailPlaceholderDefinitions,
    previewMailTemplateHtml,
  } = useMailTemplate();
  const { hasBackendPermission } = usePermissions();
  const canEdit = hasBackendPermission("communication.mail_templates.edit");
  const { showConfirmation } = React.useContext(ConfirmationModalContext);
  const { showAlert } = useAlert();

  const [typeFilter, setTypeFilter] = React.useState<MailTemplateType>("training");
  const [templates, setTemplates] = React.useState<MailTemplate[]>([]);
  const [template, setTemplate] = React.useState<MailTemplate | undefined>(undefined);
  const [blocks, setBlocks] = React.useState<MailBodyBlock[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [placeholderCatalog, setPlaceholderCatalog] = React.useState<MailPlaceholderDefinition[]>(
    [],
  );
  const [previewFullHtml, setPreviewFullHtml] = React.useState<string>("");
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [placeholderTarget, setPlaceholderTarget] =
    React.useState<MailPlaceholderInsertTarget>("body");
  const placeholderTargetRef = React.useRef<MailPlaceholderInsertTarget>("body");
  const lastRecipientFieldRef = React.useRef<"to" | "bcc">("to");
  const blockEditorRef = React.useRef<MailBodyBlockEditorHandle>(null);
  const subjectInsertRef = React.useRef<((key: string) => void) | null>(null);
  const selectedIdRef = React.useRef<number | undefined>(undefined);

  const onPlaceholderTargetChange = React.useCallback((target: MailPlaceholderInsertTarget) => {
    placeholderTargetRef.current = target;
    setPlaceholderTarget(target);

    if (target === "to" || target === "bcc") {
      lastRecipientFieldRef.current = target;
    }
  }, []);

  const registerSubjectInsert = React.useCallback((fn: ((key: string) => void) | null) => {
    subjectInsertRef.current = fn;
  }, []);

  React.useEffect(() => {
    selectedIdRef.current = template?.id;
  }, [template?.id]);

  const applyTemplate = React.useCallback((raw: MailTemplate) => {
    const { template: nextT, blocks: nextB } = hydrateTemplate(raw);

    setTemplate(nextT);
    setBlocks(nextB);
  }, []);

  const applyDraft = React.useCallback((type: MailTemplateType) => {
    const { template: nextT, blocks: nextB } = emptyDraft(type);

    setTemplate(nextT);
    setBlocks(nextB);
  }, []);

  const reloadList = React.useCallback(
    async (preferId?: number) => {
      setLoading(true);
      try {
        const list = (await listMailTemplates(typeFilter)) ?? [];
        setTemplates(list);

        const wantId = preferId ?? selectedIdRef.current;
        const found = wantId != null ? list.find((t) => t.id === wantId) : undefined;

        if (found) {
          applyTemplate(found);
        } else if (list.length > 0) {
          applyTemplate(list[0]);
        } else {
          applyDraft(typeFilter);
        }
      } finally {
        setLoading(false);
      }
    },
    [applyDraft, applyTemplate, listMailTemplates, typeFilter],
  );

  // Switching type resets the selection: a template never crosses catalogs.
  const onTypeFilterChange = React.useCallback((next: MailTemplateType) => {
    selectedIdRef.current = undefined;
    setTypeFilter(next);
  }, []);

  React.useEffect(() => {
    void reloadList();
  }, [reloadList]);

  React.useEffect(() => {
    let cancelled = false;

    void (async () => {
      const defs = await listMailPlaceholderDefinitions(typeFilter);

      if (!cancelled && defs) {
        setPlaceholderCatalog(defs);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [listMailPlaceholderDefinitions, typeFilter]);

  const handleBlocksChange = React.useCallback(
    (next: MailBodyBlock[]) => {
      setBlocks(next);
      setTemplate((prev: MailTemplate | undefined) =>
        prev ? { ...prev, html: serializeBlocks(next, placeholderCatalog) } : prev,
      );
    },
    [placeholderCatalog],
  );

  const previewSourceHtml = template?.html ?? "";
  const previewType = template?.type ?? typeFilter;

  React.useEffect(() => {
    if (!template) {
      setPreviewFullHtml("");
      setPreviewLoading(false);

      return;
    }

    let cancelled = false;

    setPreviewLoading(true);

    const timerId = window.setTimeout(() => {
      void (async () => {
        const html = await previewMailTemplateHtml(previewSourceHtml, previewType);

        if (!cancelled) {
          setPreviewFullHtml(html || "");
          setPreviewLoading(false);
        }
      })();
    }, 320);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [template, previewSourceHtml, previewType, previewMailTemplateHtml]);

  const onSelectTemplateId = React.useCallback(
    (id: number) => {
      const picked = templates.find((t) => t.id === id);
      if (picked) applyTemplate(picked);
    },
    [applyTemplate, templates],
  );

  const onNewTemplate = React.useCallback(() => {
    selectedIdRef.current = undefined;
    applyDraft(typeFilter);
  }, [applyDraft, typeFilter]);

  const onSave = React.useCallback(async () => {
    if (!template || saving) return;

    if (!template.name.trim() || !template.subject.trim()) {
      showAlert("Nom et objet requis", "warning");

      return;
    }

    const ok = await showConfirmation(
      template.id ? "Modifier le modèle" : "Créer le modèle",
      template.id ? "Enregistrer ce modèle e-mail ?" : "Créer ce modèle e-mail ?",
    );
    if (!ok) return;

    const payload: MailTemplate = {
      ...template,
      to: Array.isArray(template.to) ? template.to : [],
      bcc: Array.isArray(template.bcc) ? template.bcc : [],
    };

    setSaving(true);
    try {
      if (template.id) {
        if (await updateMailTemplate(template.id, payload)) {
          showAlert("Modèle enregistré", "success");
          await reloadList(template.id);
        }
      } else {
        const created = await createMailTemplate(payload);
        if (created?.id != null) {
          showAlert("Modèle créé", "success");
          await reloadList(created.id);
        }
      }
    } finally {
      setSaving(false);
    }
  }, [
    createMailTemplate,
    reloadList,
    saving,
    showAlert,
    showConfirmation,
    template,
    updateMailTemplate,
  ]);

  const onDuplicate = React.useCallback(async () => {
    if (!template || !canEdit) return;

    const created = await createMailTemplate({
      name: `${template.name || "Sans nom"} (copie)`,
      type: template.type,
      subject: template.subject,
      html: template.html,
      to: Array.isArray(template.to) ? template.to : [],
      bcc: Array.isArray(template.bcc) ? template.bcc : [],
    });

    if (created?.id != null) {
      showAlert("Modèle dupliqué", "success");
      await reloadList(created.id);
    }
  }, [canEdit, createMailTemplate, reloadList, showAlert, template]);

  const onDelete = React.useCallback(async () => {
    if (!template?.id) return;

    const ok = await showConfirmation(
      "Supprimer le modèle",
      `Supprimer « ${template.name} » ? Les associations qui l'utilisent seront vidées et aucun e-mail ne sera plus envoyé pour celles-ci.`,
    );
    if (!ok) return;

    await deleteMailTemplate(template.id);
    showAlert("Modèle supprimé", "success");
    selectedIdRef.current = undefined;
    await reloadList();
  }, [deleteMailTemplate, reloadList, showAlert, showConfirmation, template]);

  const insertRecipientPlaceholder = React.useCallback(
    (field: "to" | "bcc", key: string) => {
      if (!canEdit) return;

      const token = mailPhToken(key);
      setTemplate((prev) => {
        if (!prev) return prev;
        const list = (field === "to" ? prev.to : prev.bcc) ?? [];
        if (list.includes(token)) return prev;

        return field === "to"
          ? { ...prev, to: [...list, token] }
          : { ...prev, bcc: [...list, token] };
      });
    },
    [canEdit],
  );

  const insertPlaceholder = React.useCallback(
    (key: string) => {
      if (!canEdit) return;

      const kind = mailPlaceholderInsertKind(
        placeholderCatalog.find((d) => d.key === key),
        placeholderTargetRef.current,
        lastRecipientFieldRef.current,
        placeholderCatalog,
      );

      if (kind === "to" || kind === "bcc") {
        insertRecipientPlaceholder(kind, key);

        return;
      }

      if (kind === "subject") {
        subjectInsertRef.current?.(key);

        return;
      }

      if (kind === "body") {
        blockEditorRef.current?.insertToken(mailPhToken(key));
      }
    },
    [canEdit, insertRecipientPlaceholder, placeholderCatalog],
  );

  const envelopeSubjectPlaceholderChips = React.useMemo(
    () => mailEnvelopeSubjectPlaceholderChips(placeholderCatalog),
    [placeholderCatalog],
  );

  const envelopeRecipientPlaceholderChips = React.useMemo(
    () => mailEnvelopeRecipientPlaceholderChips(placeholderCatalog),
    [placeholderCatalog],
  );

  const palettePlaceholderChips = React.useMemo(
    () => mailPlaceholderPaletteChips(placeholderCatalog),
    [placeholderCatalog],
  );

  const interactivePlaceholderKeys = React.useMemo(
    () => mailPlaceholderInteractiveKeys(placeholderCatalog, placeholderTarget),
    [placeholderCatalog, placeholderTarget],
  );

  const placeholderChipAllowDrag = mailPlaceholderChipAllowDrag(placeholderTarget);

  const booleanConditionPlaceholders = React.useMemo(
    () => placeholderCatalog.filter((d) => d.type === MAIL_PLACEHOLDER_TYPE_BOOLEAN),
    [placeholderCatalog],
  );

  return {
    typeFilter,
    onTypeFilterChange,
    templates,
    template,
    setTemplate,
    blocks,
    loading,
    saving,
    onSelectTemplateId,
    placeholderCatalog,
    envelopeSubjectPlaceholderChips,
    envelopeRecipientPlaceholderChips,
    palettePlaceholderChips,
    interactivePlaceholderKeys,
    placeholderChipAllowDrag,
    booleanConditionPlaceholders,
    previewFullHtml,
    previewLoading,
    blockEditorRef,
    handleBlocksChange,
    onNewTemplate,
    onSave,
    onDuplicate,
    onDelete,
    insertPlaceholder,
    setPlaceholderTarget: onPlaceholderTargetChange,
    registerSubjectInsert,
    canEdit,
  };
}
